import type { IComponentController, IHttpService, IHttpResponse } from "angular";
import "angular-translate";
import type { Subscription } from "rxjs";
import { from, of } from "rxjs";
import { finalize, tap, flatMap } from "rxjs/operators";

import { isEmpty } from "lodash";

import type { Duration } from "moment-timezone";

import type { IProfileService } from "App/Modules/Auth";
import type { IEvent, IEventBlock, ISiteSlim } from "App/Modules/Models";
import { EventAssessmentClass, EventBlockType, EventClassification, EventType, Permission } from "App/Modules/Models";
import type { IEventsRepository } from "App/Modules/Repositories";
import { humanizeMilliseconds } from "../../../../../ReactComponents/Shared/Utils/dateFunctions";

export default class EventsTableComponentController implements IComponentController {
    public static $inject = ["$scope", "mmEventsRepository", "mmProfileService", "$http"];

    public readonly eventId: string;
    public getEvent: () => IEvent;
    public entry: IEvent;
    public readonly site: ISiteSlim;

    public isLoading = true;
    public hasImageError = false;
    public currentEventSubscription: Subscription;

    public classificationInfoString: string;
    public evaluationInfoStringArray: string[];
    public measureInfoStringArray: string[];

    public eventBlockSource: IEventBlock;
    public repeatableEventBlocks: IEventBlock[];
    public isAllEventBlocksExpanded = false;

    private eventSubscription: Subscription;

    private isSysAdmin = false;

    constructor(
        private readonly $scope: ng.IScope,
        private readonly eventsRepository: IEventsRepository,
        private readonly profileService: IProfileService,
        private readonly $http: IHttpService,
    ) {}

    public $onInit() {
        this.isSysAdmin = this.profileService.hasPermission(Permission.GlobalSysAdmin);

        this.eventSubscription = this.eventsRepository
            .getEvent(this.site.id, this.eventId)
            .pipe(
                flatMap((e) => {
                    if (e.data && !e.data.videoUrl && e.data.image.image) {
                        return from(
                            // Fetch image using access token
                            this.$http
                                .get(`${e.data.image.image.url}?timestamp=${new Date().getTime()}`, {
                                    responseType: "arraybuffer",
                                })
                                .then((imageGetResponse: IHttpResponse<ArrayBuffer>) => {
                                    // Create a blob and a URL reference to that blob
                                    const blob = new Blob([imageGetResponse.data], {
                                        type: imageGetResponse.headers("content-type") || undefined,
                                    });
                                    const url = URL.createObjectURL(blob);
                                    e.data.image.image.url = url;

                                    return e;
                                })
                                .catch(() => {
                                    this.onVideoImageLoadError();
                                    e.data.image.image.url = "";
                                    return e;
                                }),
                        );
                    }

                    return of(e);
                }),
                tap((e) => {
                    if (e.data) {
                        this.entry = e.data;
                        this.classificationInfoString = this.getClassificationInfo();
                        this.evaluationInfoStringArray = this.getEvaluationInfo();
                        this.measureInfoStringArray = this.getMeasureInfo();
                        this.eventBlockSource = this.getEventBlockSource();
                        this.repeatableEventBlocks = this.getRepeatableEventBlocks();
                    }
                }),
                finalize(() => {
                    this.$scope.$applyAsync();
                    this.isLoading = false;
                }),
            )
            .subscribe();
    }

    public $onDestroy() {
        if (this.eventSubscription) {
            this.eventSubscription.unsubscribe();
        }
    }

    public onVideoError() {
        this.entry.videoUrl = null;
    }
    private onVideoImageLoadError() {
        this.hasImageError = true;
    }

    private getClassificationInfo() {
        if (this.entry.eventAssessmentClass === EventAssessmentClass.Unknown) {
            return "Gui.Manage.SiteEventsTable.Details.PendingClassification";
        }

        switch (this.entry.eventClassification) {
            case EventClassification.L2HumanActivityNo:
                // Only visible for sysadmins, so Ok with special string
                return "Gui.Manage.SiteEventsTable.Details.L2HumanActivityNo";
            case EventClassification.L2HumanActivityUncertain:
                // Only visible for sysadmins, so Ok with special string
                return "Gui.Manage.SiteEventsTable.Details.L2HumanActivityUncertain";
            case EventClassification.L2HumanActivityYes:
                // Visible for everyone, so no special string here.
                // We could show a special string here, like "Gui.Manage.SiteEventsTable.Details.L2HumanActivityYes"
                // only for sysadmins, if possible. But perhaps better to show this distinction in some other way.
                return "Gui.Manage.SiteEventsTable.Details.HumanActivity";
            case EventClassification.FlameNo:
                return "Gui.Manage.SiteEventsTable.Details.FlameNo";
            case EventClassification.FlameYes:
                return "Gui.Manage.SiteEventsTable.Details.FlameYes";
            case EventClassification.LoiteringHumanActivityNo:
                return "Gui.Manage.SiteEventsTable.Details.LoiteringHumanActivityNo";
            case EventClassification.LoiteringHumanActivityYes:
                return "Gui.Manage.SiteEventsTable.Details.LoiteringHumanActivityYes";
            case EventClassification.Human:
                return "Gui.Manage.SiteEventsTable.Details.HumanActivity";
            case EventClassification.ViolentBehaviorNo:
                return "Gui.Manage.SiteEventsTable.Details.ViolentBehaviorNo";
            case EventClassification.ViolentBehaviorYes:
                return "Gui.Manage.SiteEventsTable.Details.ViolentBehaviorYes";
            case EventClassification.FallNo:
                return "Gui.Manage.SiteEventsTable.Details.FallNo";
            case EventClassification.FallYes:
                return "Gui.Manage.SiteEventsTable.Details.FallYes";
            case EventClassification.RailDefenderNo:
                return "Gui.Manage.SiteEventsTable.Details.RailDefenderNo";
            case EventClassification.RailDefenderYes:
                return "Gui.Manage.SiteEventsTable.Details.RailDefenderYes";
            case EventClassification.UnattendedObjectNo:
                return "Gui.Manage.SiteEventsTable.Details.UnattendedObjectNo";
            case EventClassification.UnattendedObjectYes:
                return "Gui.Manage.SiteEventsTable.Details.UnattendedObjectYes";
            default:
                return "Gui.Manage.SiteEventsTable.Details.NoHumanActivity";
        }
    }

    private getEvaluationInfo() {
        let result: string[];
        if (this.entry.eventAssessmentClass === EventAssessmentClass.Unknown) {
            result = ["Gui.Manage.SiteEventsTable.Details.PendingClassification"];
        } else if (
            this.entry.eventAssessmentClass === EventAssessmentClass.NoAssessmentNeeded ||
            this.entry.eventAssessmentClass === EventAssessmentClass.NoClassificationNeeded
        ) {
            result = ["Gui.Manage.SiteEventsTable.Details.NoEvaluationNeeded"];
        } else if (!isEmpty(this.entry.decisionMakingDetails)) {
            result = this.entry.decisionMakingDetails.split(/\r\n/g).filter((infoString) => !!infoString);
        }
        return result;
    }

    private getMeasureInfo() {
        let result: string[];
        if (this.entry.eventAssessmentClass === EventAssessmentClass.Unknown) {
            result = ["Gui.Manage.SiteEventsTable.Details.PendingClassification"];
        } else if (this.entry.eventAssessmentClass !== EventAssessmentClass.MeasureTaken) {
            result = ["Gui.Manage.SiteEventsTable.Details.NoMeasureTaken"];
        } else if (!isEmpty(this.entry.measureTakingDetails)) {
            result = this.entry.measureTakingDetails.split(/\r\n/g).filter((infoString) => !!infoString);
        }
        return result;
    }

    private getEventBlockSource() {
        // The assumption here is that Source can't be repeated and can be displayed by itself
        if (this.entry.eventBlocks) {
            const sourceBlock = this.entry.eventBlocks.find(
                (eventBlock) => eventBlock.eventBlockType === EventBlockType.Source,
            );

            return sourceBlock;
        }

        return null;
    }

    private getRepeatableEventBlocks() {
        // The assumption here is that all blocks except Source can be repeated
        if (this.entry.eventBlocks) {
            const repeatableBlocks = this.entry.eventBlocks.filter(
                (eventBlock) => eventBlock.eventBlockType !== EventBlockType.Source,
            );

            return repeatableBlocks;
        }

        return null;
    }

    private getHumanizedDuration(value: Duration) {
        if (value) {
            return humanizeMilliseconds(value.asMilliseconds());
        }

        return "Gui.Manage.DurationNA";
    }

    private toggleCollapsedClass(event: any) {
        event.target.closest(".collapsable-list-item").classList.toggle("collapsable-list-item--active");

        const items = event.target.closest(".collapsable-list").querySelectorAll(".collapsable-list-item");

        this.isAllEventBlocksExpanded = Array.from(items)
            .filter((elem: HTMLElement) => {
                return !!elem.querySelector(".collapsable-list-content");
            })
            .every((item: HTMLElement) => item.classList.contains("collapsable-list-item--active"));
    }

    private toggleAllCollapsableClasses(event: any) {
        const items = event.target.closest(".collapsable-list").querySelectorAll(".collapsable-list-item");

        if (this.isAllEventBlocksExpanded) {
            items.forEach((item) => item.classList.remove("collapsable-list-item--active"));
        } else {
            items.forEach((item) => item.classList.add("collapsable-list-item--active"));
        }

        this.isAllEventBlocksExpanded = !this.isAllEventBlocksExpanded;
    }

    private isPictureType(event: IEvent) {
        if (event) {
            switch (event.eventType) {
                case EventType.VideoFallDetection:
                case EventType.VideoMotionDetection:
                case EventType.VideoLoiteringDetection:
                case EventType.VideoRailDefenderDetection:
                case EventType.VideoViolentBehaviorDetection:
                case EventType.VideoUnattendedObjectDetection:
                case EventType.VideoFireDetection:
                case EventType.SiteTestAlarmSending:
                case EventType.SiteTestFullAlarmFlow:
                    return true;
                default:
                    return false;
            }
        }

        return false;
    }
}
